import { makeObservable, observable, action, computed } from 'mobx'
import axios from 'axios'
import { BaseStore } from './BaseStore'
import type { User, Plan } from '@core/models'
import { AdminRole, RootPlan } from '@core/models'
import { events } from './index'

export enum AuthStatus {
  INITIAL = 'INITIAL',
  UNAUTHORIZED = 'UNAUTHORIZED',
  AUTHORIZED = 'AUTHORIZED',
}

interface AuthResponse {
  user: User
}

export class AuthStore extends BaseStore {
  authStatus = AuthStatus.INITIAL
  user: User | null = null
  originalTenantId = ''
  currentTenantPlan: Plan | null = null

  constructor() {
    super()
    makeObservable(this, {
      authStatus: observable,
      user: observable,
      originalTenantId: observable,
      currentTenantPlan: observable,
      login: action,
      logout: action,
      checkAuth: action,
      startSpoofing: action,
      endSpoofing: action,
      setCurrentTenantPlan: action,
      isAuthenticated: computed,
      isRootTenant: computed,
      isSpoofing: computed,
      currentTenantId: computed
    })
    // checkAuth()はApp.tsxのuseEffectで呼ぶため、ここでは呼ばない
  }

  async login(email: string, password: string, keepSession: boolean) {
    const result = await this.handleApiCall(async () => {
      const response = await axios.post<AuthResponse>(
        this.apiPath('v1/auth'),
        { email, password, keepSession },
        { 
          headers: this.generateFetchHeader(),
          withCredentials: true
        }
      )
      return response.data
    })

    if (result) {
      this.user = result.user
      this.authStatus = AuthStatus.AUTHORIZED
      this.tryShowToast('ログインしました', 'success')
      return true
    }

    this.tryShowToast('ログインに失敗しました', 'error')
    return false
  }

  async logout() {
    await this.handleApiCall(async () => {
      await axios.delete(
        this.apiPath('v1/auth'),
        { 
          headers: this.generateFetchHeader(),
          withCredentials: true
        }
      )
    })
    
    this.authStatus = AuthStatus.UNAUTHORIZED
    this.user = null
    delete localStorage.spoofingTenantId
  }

  async checkAuth() {
    const result = await this.handleApiCall(async () => {
      const response = await axios.get<User>(
        this.apiPath('v1/auth'),
        { 
          headers: this.generateFetchHeader(),
          withCredentials: true
        }
      )
      return response.data
    })

    if (result) {
      this.user = result
      this.authStatus = AuthStatus.AUTHORIZED
      
      // スプーフィング状態の復元（同期的に実行）
      const spoofingTenantId = localStorage.spoofingTenantId
      if (spoofingTenantId && this.user) {
        // 元のテナントIDを保存
        this.originalTenantId = this.user.tenant
        // なりすまし先のテナントIDを設定
        this.user = {
          ...this.user,
          tenant: spoofingTenantId
        }
      }
      
      return true
    }

    this.authStatus = AuthStatus.UNAUTHORIZED
    return false
  }

  startSpoofing(tenantId: string) {
    if (!this.user) return
    
    if (!this.originalTenantId) {
      this.originalTenantId = this.user.tenant
    }
    
    this.user = {
      ...this.user,
      tenant: tenantId
    }
    
    localStorage.spoofingTenantId = tenantId
    events.emit('changeTenantId', tenantId)
    this.tryShowToast('テナントを切り替えました', 'success')
  }

  endSpoofing() {
    if (!this.user || !this.originalTenantId) return
    
    const originalTenant = this.originalTenantId
    this.user = {
      ...this.user,
      tenant: originalTenant
    }
    
    this.originalTenantId = ''
    delete localStorage.spoofingTenantId
    events.emit('changeTenantId', originalTenant)
    this.tryShowToast('元のテナントに戻りました', 'success')
  }

  get isAuthenticated() {
    return this.authStatus === AuthStatus.AUTHORIZED
  }

  setCurrentTenantPlan(plan: Plan | null) {
    this.currentTenantPlan = plan
  }

  get isRootTenant() {
    // permission.goと同じ判定: user.Role == AdminRole && tenant.Plan == RootPlan
    return this.user?.role === AdminRole && this.currentTenantPlan === RootPlan
  }

  get isSpoofing() {
    return this.originalTenantId !== ''
  }

  get currentTenantId() {
    return this.user?.tenant || ''
  }
}